import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { CognitoService } from '../service/cognito.service';
import { ThemeService } from '../service/theme.service';
import { RoleService } from '../service/role.service';
import { SystemMessageService } from '../service/system-message.service';
import { environment } from '../environments/environment';

interface ApiKeyResponse {
  api_key?: string;
}

@Component({
  selector: 'app-operator-api-key',
  templateUrl: './operator-api-key.component.html',
  styleUrls: ['./operator-api-key.component.css', '../../global-elements.css'],
  standalone: false
})
export class OperatorApiKeyComponent implements OnInit {
  public operatorId = '';
  public apiKeyValue = '';
  public loading = false;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
    public theme: ThemeService,
    public roleService: RoleService,
    public systemMessageService: SystemMessageService
  ) {}

  async ngOnInit() {
    await this.cognitoService.confirmValidUser();
    await this.cognitoService.getUserType();

    await this.cognitoService.getCurrentRole(
      [environment.users.role.dispatch, environment.users.role.administrator],
      [environment.users.maintenance],
      true,
      this.roleService.roles
    );

    const currentUser = await this.cognitoService.getUser();
    this.operatorId =
      currentUser?.attributes?.['custom:operations_id'] ||
      currentUser?.attributes?.['custom:operator_id'] ||
      '';

    if (!this.operatorId) {
      this.apiKeyValue = '';
      return;
    }

    await this.loadApiKeyByOperatorId();
  }

  async loadApiKeyByOperatorId() {
    this.loading = true;
    try {
      const url = environment.api.stage + environment.api.route.getApiKey;
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const response = await firstValueFrom(
        this.http.post<ApiKeyResponse>(url, { id: this.operatorId }, { headers })
      );
      const key = response?.api_key?.trim();
      this.apiKeyValue = key ? key : '';
    } catch (error) {
      this.apiKeyValue = '';
      console.error('Error retrieving operator API key:', error);
    } finally {
      this.loading = false;
    }
  }

  get maskedApiKey(): string {
    return this.apiKeyValue ? '*'.repeat(this.apiKeyValue.length) : '';
  }

  get hasApiKey(): boolean {
    return !!this.apiKeyValue;
  }

  get hasOperatorId(): boolean {
    return !!this.operatorId;
  }

  async copyOperatorId(): Promise<void> {
    if (!this.operatorId) {
      return;
    }

    try {
      await navigator.clipboard.writeText(this.operatorId);
      this.systemMessageService.selectRibbon('success', 'alert-success-generic-message');
    } catch (error) {
      this.systemMessageService.selectRibbon('danger', 'alert-danger-generic-message');
      console.error('Failed to copy operator ID:', error);
    }
  }

  async copyApiKey(): Promise<void> {
    if (!this.apiKeyValue) {
      return;
    }

    try {
      await navigator.clipboard.writeText(this.apiKeyValue);
      this.systemMessageService.selectRibbon('success', 'alert-success-generic-message');
    } catch (error) {
      this.systemMessageService.selectRibbon('danger', 'alert-danger-generic-message');
      console.error('Failed to copy API key:', error);
    }
  }
}
